<template>
  <nav
    ref="root"
    class="quick-navigation flex-column"
    :aria-label="props.title"
    data-qa="quick-navigation"
  >
    <span class="quick-navigation__title typo-hint text-bold color-text-secondary">{{ props.title }}</span>
    <NavigationMenu
      :items="navigationMenuItems"
      :active-key="activeAnchorId"
      aria-current-value="location"
      class="quick-navigation__menu"
      @select="selectAnchor"
    >
      <template
        #group-title="{
          label,
          count,
        }"
      >
        <span class="quick-navigation__group-title flex-row justify-spaced typo-hint text-bold color-text-secondary">
          <span>{{ label }}</span>
          <span v-if="count !== undefined">{{ count }}</span>
        </span>
      </template>
    </NavigationMenu>
  </nav>
</template>

<script setup lang="ts">
/**
 * Lists the anchors of a page as links, jumps to them on click and marks the one being read.
 *
 * - `items`: the anchors; `anchorId` is the id of the element to jump to.
 * - `v-model:active-anchor-id`: the anchor being read, set while scrolling.
 * - `offset`: distance from the top of the viewport at which an element counts as being read, e.g. the height of a
 *   sticky header; the scroll-margin-top of the element is added to it.
 * - `updatesUrlHash`: writes the active anchor into the URL, so a copied URL opens the same position. Opening a URL
 *   with an anchor jumps to it either way.
 * - `createHash` and `parseHash`: the format of the anchor in the URL, `#anchorId` by default, for pages that use
 *   the hash for more than the anchor.
 */
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  useTemplateRef,
  watch,
} from 'vue';

import type {
  INavigationMenuGroup,
  TNavigationMenuItem,
} from '../navigation-menu/navigation-menu.ts';
import NavigationMenu from '../navigation-menu/navigation-menu.vue';

import type {
  IQuickNavigationItem,
} from './quick-navigation.ts';
import {
  createAnchorHash,
  parseAnchorHash,
} from './quick-navigation.ts';

const props = withDefaults(defineProps<{
  title: string,
  items: IQuickNavigationItem[],
  offset?: number,
  updatesUrlHash?: boolean,
  createHash?: (anchorId: string) => string,
  parseHash?: (hash: string) => string,
}>(), {
  offset: 120,
  updatesUrlHash: false,
  createHash: createAnchorHash,
  parseHash: parseAnchorHash,
});

const activeAnchorId = defineModel<string | undefined>('activeAnchorId');

const navigationMenuItems = computed<TNavigationMenuItem[]>(() => {
  const ungroupedEntries: TNavigationMenuItem[] = [];
  const groupsByLabel = new Map<string, INavigationMenuGroup>();
  props.items.forEach((quickNavigationItem) => {
    const navigationMenuEntry = {
      key: quickNavigationItem.anchorId,
      label: quickNavigationItem.label,
      count: quickNavigationItem.count,
      detail: quickNavigationItem.detail,
      href: props.createHash(quickNavigationItem.anchorId),
    };
    if (quickNavigationItem.group === undefined) {
      ungroupedEntries.push(navigationMenuEntry);
      return;
    }
    const navigationMenuGroup = groupsByLabel.get(quickNavigationItem.group) ?? {
      label: quickNavigationItem.group,
      entries: [],
    };
    navigationMenuGroup.entries.push(navigationMenuEntry);
    groupsByLabel.set(quickNavigationItem.group, navigationMenuGroup);
  });
  return [
    ...ungroupedEntries,
    ...groupsByLabel.values(),
  ];
});

const rootElement = useTemplateRef<HTMLElement>('root');

/**
 * The element that scrolls the entries when they do not fit, e.g. a sticky side bar. Elements that scroll the
 * anchors as well are the page, which must keep its position.
 */
function findEntryScrollContainer(entryElement: HTMLElement, anchorElement: HTMLElement): HTMLElement | undefined {
  for (let ancestor = entryElement.parentElement; ancestor; ancestor = ancestor.parentElement) {
    if (ancestor.contains(anchorElement)) {
      return undefined;
    }
    const overflowY = getComputedStyle(ancestor).overflowY;
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (isScrollable && ancestor.scrollHeight > ancestor.clientHeight) {
      return ancestor;
    }
  }
  return undefined;
}

/** Without it, the entry being read can be outside of a long list, and the reader has to scroll the list as well. */
function revealActiveEntry() {
  const activeEntryElement = rootElement.value?.querySelector<HTMLElement>('[aria-current]');
  const anchorElement = activeAnchorId.value ? document.getElementById(activeAnchorId.value) : null;
  if (!activeEntryElement || !anchorElement) {
    return;
  }
  const scrollContainer = findEntryScrollContainer(activeEntryElement, anchorElement);
  if (!scrollContainer) {
    return;
  }
  const entryRectangle = activeEntryElement.getBoundingClientRect();
  const containerRectangle = scrollContainer.getBoundingClientRect();
  if (entryRectangle.top < containerRectangle.top) {
    scrollContainer.scrollTop -= containerRectangle.top - entryRectangle.top;
  } else if (entryRectangle.bottom > containerRectangle.bottom) {
    scrollContainer.scrollTop += entryRectangle.bottom - containerRectangle.bottom;
  }
}

function writeUrlHash(anchorId: string) {
  const hash = props.createHash(anchorId);
  if (props.updatesUrlHash && window.location.hash !== hash) {
    // replaces the entry instead of adding one per section and keeps its state, which a router may rely on
    window.history.replaceState(window.history.state, '', hash);
  }
}

function setActiveAnchorId(anchorId: string | undefined) {
  if (anchorId === undefined || anchorId === activeAnchorId.value) {
    return;
  }
  activeAnchorId.value = anchorId;
  writeUrlHash(anchorId);
}

function findReadAnchorId(): string | undefined {
  const lastItem = props.items.at(-1);
  const lastElement = lastItem ? document.getElementById(lastItem.anchorId) : null;
  // The last elements may be too short to ever reach the top of the viewport.
  if (lastItem && lastElement && lastElement.getBoundingClientRect().bottom <= window.innerHeight) {
    return lastItem.anchorId;
  }
  const reachedItems = props.items.filter((quickNavigationItem) => {
    const anchorElement = document.getElementById(quickNavigationItem.anchorId);
    if (anchorElement === null) {
      return false;
    }
    // An element that was jumped to stops at its scroll margin, so the margin counts as reached as well.
    const scrollMarginTop = Number.parseFloat(getComputedStyle(anchorElement).scrollMarginTop) || 0;
    return anchorElement.getBoundingClientRect().top < props.offset + scrollMarginTop;
  });
  return reachedItems.at(-1)?.anchorId ?? props.items[0]?.anchorId;
}

function updateActiveAnchorId() {
  setActiveAnchorId(findReadAnchorId());
}

function scrollToAnchor(anchorId: string, behavior: ScrollBehavior) {
  document.getElementById(anchorId)?.scrollIntoView({
    behavior,
    block: 'start',
  });
}

function selectAnchor(anchorId: string) {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  scrollToAnchor(anchorId, prefersReducedMotion ? 'auto' : 'smooth');
  setActiveAnchorId(anchorId);
}

/** Opening a URL with the anchor of an item jumps to its element. */
function jumpToUrlHash(): boolean {
  const anchorId = props.parseHash(window.location.hash);
  if (!props.items.some((quickNavigationItem) => quickNavigationItem.anchorId === anchorId)) {
    return false;
  }
  scrollToAnchor(anchorId, 'auto');
  activeAnchorId.value = anchorId;
  return true;
}

watch(() => props.items, updateActiveAnchorId);
watch(activeAnchorId, revealActiveEntry, {
  flush: 'post',
});

// The page decides which element scrolls, so scroll events are caught for the whole document.
onMounted(async () => {
  document.addEventListener('scroll', updateActiveAnchorId, {
    capture: true,
    passive: true,
  });
  // the content next to the navigation renders in the same tick
  await nextTick();
  // without an anchor in the URL, the page keeps its URL until the reader scrolls or clicks
  if (!jumpToUrlHash()) {
    activeAnchorId.value = findReadAnchorId();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('scroll', updateActiveAnchorId, {
    capture: true,
  });
});
</script>

<style lang="scss" scoped>
.quick-navigation {
  &__title {
    text-transform: uppercase;
  }

  // A page lists many anchors, which fit next to its content only when packed tightly.
  &__menu {
    --navigation-menu-entry-padding: var(--gap-union) var(--padding-item);
    --navigation-menu-entry-spacing: 1px;
    --navigation-menu-icon-gap: var(--gap-union);
    --navigation-menu-group-title-margin: var(--gap) 0 var(--gap-union);
  }

  &__group-title {
    padding: 0 var(--padding-item);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
}
</style>
